import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import {
  SCreateKoboProject,
  SUpdateKoboProject,
} from "../../../../apis/app.service";
import { IKoboProject, IKoboProjectPayload } from "../../core/models";
import "./CreateEditKoboProject.scss";

interface CreateEditKoboProjectProps {
  project?: IKoboProject;
  onClose: () => void;
}

const validationSchema = Yup.object({
  name: Yup.string().required("Project name is required"),
  asset_uid: Yup.string().required("Asset UID is required"),
  description: Yup.string(),
});

const CreateEditKoboProject: React.FC<CreateEditKoboProjectProps> = ({
  project,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const isEditMode = !!project;

  const createMutation = useMutation({
    mutationFn: (values: IKoboProjectPayload) => SCreateKoboProject(values),
    onSuccess: (response) => {
      toast.success(response.message);
      queryClient.invalidateQueries({ queryKey: ["koboProjects"] });
      onClose();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (values: {
      projectId: string;
      body: Partial<IKoboProjectPayload>;
    }) => SUpdateKoboProject(values),
    onSuccess: (response) => {
      toast.success(response.message);
      queryClient.invalidateQueries({ queryKey: ["koboProjects"] });
      onClose();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const initialValues: IKoboProjectPayload = {
    name: project?.name || "",
    asset_uid: project?.asset_uid || "",
    description: project?.description || "",
  };

  const handleSubmit = (values: IKoboProjectPayload) => {
    if (isEditMode) {
      updateMutation.mutate({ projectId: project._id, body: values });
    } else {
      createMutation.mutate(values);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {isEditMode ? "Edit Kobo Project" : "Create New Kobo Project"}
          </h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting }) => (
            <Form className="kobo-form">
              <div className="form-group">
                <label htmlFor="name">
                  Project Name <span className="required">*</span>
                </label>
                <Field
                  type="text"
                  id="name"
                  name="name"
                  className="form-control"
                  placeholder="Enter project name"
                />
                <ErrorMessage
                  name="name"
                  component="div"
                  className="error-message"
                />
              </div>

              <div className="form-group">
                <label htmlFor="asset_uid">
                  Asset UID <span className="required">*</span>
                </label>
                <Field
                  type="text"
                  id="asset_uid"
                  name="asset_uid"
                  className="form-control"
                  placeholder="e.g., a5YEedAfMUvAxdxVkSCvfd"
                  disabled={isEditMode}
                />
                <small className="form-hint">
                  Found in your KoboToolbox project URL after "forms/"
                </small>
                <ErrorMessage
                  name="asset_uid"
                  component="div"
                  className="error-message"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <Field
                  as="textarea"
                  id="description"
                  name="description"
                  className="form-control"
                  rows={3}
                  placeholder="Optional description of this project"
                />
                <ErrorMessage
                  name="description"
                  component="div"
                  className="error-message"
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={
                    isSubmitting ||
                    createMutation.isPending ||
                    updateMutation.isPending
                  }
                >
                  {isSubmitting ||
                  createMutation.isPending ||
                  updateMutation.isPending
                    ? "Saving..."
                    : isEditMode
                    ? "Update Project"
                    : "Create Project"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default CreateEditKoboProject;
